import type { Filters } from '../home.ts'
import type { AppContext } from 'types/common.js'

import component from './SalesOrder.svelte'
import { graphql } from '$houdini'
import { getSession } from 'stores/session'
import { stringToBoolean as toBool } from '@isoftdata/utility-string'
import { getDefaultDateRange } from '../home'

const salesOrderQuery = graphql(`
	query HomeSalesOrders($filter: SalesOrderFilter) {
		salesOrders(filter: $filter) {
			items {
				store {
					id
				}
				salesOrderId
				date
				billingAddress {
					company
					contact
				}
				balance
				salesperson {
					name
				}
				counterperson {
					name
				}
				total
				documentType {
					name
				}
				purchaseOrderNumber
				shippingMethod {
					name
				}
				truckNumber
				internalComments
				finalized
				totalQuantity
				totalQuantityReady
			}
		}
		salesOrderDocumentTypes {
			name
		}
	}
`)

const defaults = graphql(`
	query HomeSalesOrderDefaults {
		settingValues {
			home {
				defaultSalesOrderHistoryDateRange
			}
		}
	}
`)

export interface SalesOrderFilters extends Filters {
	showClosed: boolean
	documentType?: string
}

export default function ({ mediator, stateRouter }: AppContext) {
	stateRouter.addState({
		name: 'app.home.sales-order',
		route: 'sales-order',
		querystringParameters: ['fromDate', 'toDate', 'userId', 'storeId', 'showClosed', 'documentType'],
		defaultParameters: {
			userId: () => getSession()?.userAccountId?.toString() ?? null,
			storeId: () => getSession()?.currentStore?.toString() ?? null,
			showClosed: 'false',
			documentType: 'Invoice',
		},
		template: {
			svelte: true,
			component,
		},
		async resolve(data, parameters) {
			if (parameters.fromDate === undefined && parameters.toDate === undefined) {
				const res = await defaults.fetch()
				const defaultDateRange = getDefaultDateRange(res?.data?.settingValues?.home?.defaultSalesOrderHistoryDateRange)

				throw {
					redirectTo: {
						name: null,
						params: {
							fromDate: defaultDateRange.from,
							toDate: defaultDateRange.to,
						},
					},
				}
			}

			const filters: SalesOrderFilters = {
				userId: parameters.userId ? parseInt(parameters.userId, 10) || undefined : undefined,
				storeId: parameters.storeId ? parseInt(parameters.storeId, 10) || undefined : undefined,
				showClosed: toBool(parameters.showClosed),
				documentType: parameters.documentType,
				dates: {
					from: parameters.fromDate,
					to: parameters.toDate,
				},
			}

			const res = await salesOrderQuery.fetch({
				variables: {
					filter: {
						date: {
							gte: filters.dates.from ? new Date(filters.dates.from) : undefined,
							lte: filters.dates.to ? new Date(filters.dates.to) : undefined,
						},
						documentTypeFilter: {
							name: filters.documentType,
						},
						finalized: filters.showClosed ? null : false,
						counterpersonUserId: filters.userId ? [filters.userId] : undefined,
						storeId: filters.storeId ? [filters.storeId] : undefined,
					},
				},
			})

			return {
				salesOrders: res?.data?.salesOrders?.items ?? [],
				documentTypes: res?.data?.salesOrderDocumentTypes?.map(type => type.name) ?? [],
				...filters,
			}
		},
	})
}
